/**
 * Hook compatibility shim for react-router-dom v4.4-beta.
 *
 * This codebase pins to RR v4 (see package.json), which predates the
 * official `useHistory` / `useParams` hooks introduced in v5. The library
 * does expose its internal `__RouterContext` though, so we can build a
 * minimal hooks API ourselves and avoid an upgrade.
 *
 * Drop-in replacement — components write:
 *
 *   import { useHistory, useParams } from '../../utils/reactRouterHooks';
 *
 * instead of importing from 'react-router-dom'. Same API surface for
 * the read-only paths we use (push / replace / params).
 */
import { useContext } from 'react';
// eslint-disable-next-line import/no-internal-modules
import { __RouterContext } from 'react-router';

interface History {
  push: (path: string, state?: unknown) => void;
  replace: (path: string, state?: unknown) => void;
  goBack: () => void;
  location: { pathname: string; search: string; hash: string; state?: unknown };
}

export function useHistory(): History {
  const ctx = useContext(__RouterContext);
  if (!ctx?.history) {
    throw new Error('useHistory must be used inside a <Router>');
  }
  return ctx.history as History;
}

export function useParams<T extends Record<string, string> = Record<string, string>>(): T {
  const ctx = useContext(__RouterContext);
  return (ctx?.match?.params ?? {}) as unknown as T;
}

export function useLocation(): History['location'] {
  const ctx = useContext(__RouterContext);
  if (!ctx?.location) {
    throw new Error('useLocation must be used inside a <Router>');
  }
  return ctx.location as History['location'];
}
